import styled from '@emotion/styled';
import { Popper } from '@workday/canvas-kit-react/popup';
import { CanvasSystemIcon } from '@workday/design-assets-types';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { ZIndex } from '../styles/ZIndex';
import DevsiteToast, { type DevsiteToastAction, type ToastVariant } from './DevsiteToast';

export type { ToastVariant } from './DevsiteToast';

const TOAST_TIMEOUT = 5000;

export interface ToastOptions {
  /** Keeps the toast open until it is dismissed, instead of auto-closing after 5 seconds. */
  persistent?: boolean;
  action?: DevsiteToastAction;
  hideCloseIcon?: boolean;
  onDismiss?: () => void;
  /** Escape hatch for a truly unique case. Prefer a standard variant. */
  icon?: CanvasSystemIcon;
  /** Defaults to the variant's color, so an icon override still reads as its variant. */
  iconColor?: string;
}

interface ToastProps extends ToastOptions {
  id: number;
  cleanUp: (key: number) => void;
  message: React.ReactNode;
  variant: ToastVariant;
}

const Toast = (props: ToastProps) => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const cleanUp = () => {
    props.onDismiss?.();
    props.cleanUp(props.id);
  };

  useEffect(() => {
    const setTimeoutIfVisible = () => {
      if (document.visibilityState === 'visible') {
        timerRef.current = setTimeout(cleanUp, TOAST_TIMEOUT);
        document.removeEventListener('visibilitychange', setTimeoutIfVisible);
      }
    };

    if (!props.persistent) {
      if (document.visibilityState === 'visible') {
        timerRef.current = setTimeout(cleanUp, TOAST_TIMEOUT);
      } else {
        document.addEventListener('visibilitychange', setTimeoutIfVisible);
      }
    }

    return () => {
      // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
      clearTimeout(timerRef.current);
      document.removeEventListener('visibilitychange', setTimeoutIfVisible);
    };
  }, []);

  return (
    <DevsiteToast
      variant={props.variant}
      icon={props.icon}
      iconColor={props.iconColor}
      action={props.action}
      onClose={cleanUp}
      hideCloseIcon={props.hideCloseIcon}
      aria-live='polite'
    >
      {props.message}
    </DevsiteToast>
  );
};

/**
 * Every method surfaces a toast and returns its id so callers can later dismiss that specific toast.
 */
export interface ToastContextModel {
  /** The user's action finished. */
  success: (message: React.ReactNode, options?: ToastOptions) => number;
  /** The action was accepted and is still running, or the message is neutral news. */
  info: (message: React.ReactNode, options?: ToastOptions) => number;
  /** Something degraded or needs attention, but nothing failed. */
  warn: (message: React.ReactNode, options?: ToastOptions) => number;
  /** The action failed. */
  error: (message: React.ReactNode, options?: ToastOptions) => number;
  /** For callers that receive the variant as data, such as the admin-ui and malt-ui `displayToast` bridges. */
  show: (message: React.ReactNode, variant: ToastVariant, options?: ToastOptions) => number;
  clearToasts: () => void;
  /** Dismisses a single toast by the id returned from success/info/warn/error/show, leaving others untouched. */
  dismiss: (id: number) => void;
}

export const ToastContext = React.createContext<ToastContextModel>({
  // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
  success: null,
  // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
  info: null,
  // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
  warn: null,
  // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
  error: null,
  // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
  show: null,
  // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
  clearToasts: null,
  // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
  dismiss: null,
});

interface ToastProviderProps {
  clearOnPageChange?: boolean;
}

const ToastProvider: React.FC<React.PropsWithChildren<ToastProviderProps>> = ({ clearOnPageChange, children }) => {
  const [messages, setMessages] = useState<ToastProps[]>([]);
  const toastCount = useRef(0);
  const toastAnchorRef = useRef<HTMLDivElement>(null);

  const location = useLocation();
  const path = useRef<string>(location.pathname);
  useEffect(() => {
    // clear toasts on page change
    if (clearOnPageChange && path.current !== location.pathname) {
      path.current = location.pathname;
      setMessages([]);
    }
  });

  const cleanUp = useCallback((key: number) => {
    setMessages((messagesState) => {
      const idx = messagesState.findIndex((msg) => msg.id === key);
      return [...messagesState.slice(0, idx), ...messagesState.slice(idx + 1)];
    });
  }, []);

  const show = useCallback(
    (message: React.ReactNode, variant: ToastVariant, options?: ToastOptions) => {
      const id = toastCount.current;
      setMessages((messagesState) => [...messagesState, { ...options, id, message, variant, cleanUp }]);
      toastCount.current += 1;
      return id;
    },
    [cleanUp]
  );

  const success = useCallback(
    (message: React.ReactNode, options?: ToastOptions) => show(message, 'success', options),
    [show]
  );

  const info = useCallback(
    (message: React.ReactNode, options?: ToastOptions) => show(message, 'info', options),
    [show]
  );

  const warn = useCallback(
    (message: React.ReactNode, options?: ToastOptions) => show(message, 'warn', options),
    [show]
  );

  const error = useCallback(
    (message: React.ReactNode, options?: ToastOptions) => show(message, 'error', options),
    [show]
  );

  const clearToasts = useCallback(() => {
    setMessages([]);
  }, []);

  const context = useMemo(
    () => ({ success, info, warn, error, show, clearToasts, dismiss: cleanUp }),
    [success, info, warn, error, show, clearToasts, cleanUp]
  );

  return (
    <ToastContext.Provider value={context}>
      {children}
      <div ref={toastAnchorRef}>
        {!!messages.length && (
          <Main>
            <Popper placement='top' anchorElement={toastAnchorRef} portal={false}>
              {messages.map((msgItem: ToastProps) => (
                <Toast key={msgItem.id} {...msgItem} />
              ))}
            </Popper>
          </Main>
        )}
      </div>
    </ToastContext.Provider>
  );
};

const Main = styled.div`
  left: 0;
  position: fixed;
  right: 0;
  top: 45px;
  width: 100%;
  height: 0; /* overflow is hidden but we don't want this wide div to block other site content */
  z-index: ${ZIndex.toast};
`;

export default ToastProvider;
